import React, { useEffect, useState } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import ScrollToTop from '../../routes/ScrollToTop';
import { tenantApi } from '../../api/tenantApi';
import { useTenant } from '../../context/TenantContext';
import { GlobalSearch, useGlobalSearchOpen } from '../../components/GlobalSearch';
import { colors } from '../../theme/tokens';
import { useCurrentUser } from '../../auth/useCurrentUser';

import Sidebar from './sidebar/Sidebar';
import TopBar from './TopBar';
import ReadOnlyBanner from './ReadOnlyBanner';
import CrossEnvBanner from './CrossEnvBanner';
import ReadOnlyGuard from '../../context/ReadOnlyGuard';

const DRAWER_WIDTH = 308;

const TenantLayout: React.FC = () => {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const location = useLocation();
  const { setActiveTenantCode } = useTenant();
  const { rbacV2Enabled, hasPermission } = useCurrentUser();
  // Hide-chrome (decisions §11, CPT-1177): a `hide_chrome` holder keeps the
  // full sidebar (including UserBlock) but NOT the TopBar (env picker, search,
  // Publish button). Analytics clients need the nav and their account block;
  // they do not need the env/version chrome. Never implied by super-admin.
  const bareChrome = rbacV2Enabled && hasPermission('hide_chrome');
  const theme = useTheme();
  // `md` covers both the historical 1024px tablet breakpoint and smaller.
  // Replaces hand-rolled `useMediaQuery('(max-width:768/1024px)')` + the
  // `isMobile || isTablet` repetition.
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useGlobalSearchOpen();

  // Sync URL tenant code → context (keeps the tenant picker's localStorage
  // entry current). The setter has a no-op guard for identical values, so
  // this effect only actually fires when the URL tenant changes.
  useEffect(() => {
    if (tenantCode) setActiveTenantCode(tenantCode);
  }, [tenantCode, setActiveTenantCode]);

  // Close mobile drawer on any navigation.
  useEffect(() => {
    if (isCompact) setMobileOpen(false);
  }, [location.pathname, isCompact]);

  const { data: tenant } = useQuery({
    queryKey: ['tenant', tenantCode],
    queryFn: () => tenantApi.getByCode(tenantCode!),
    enabled: !!tenantCode,
  });

  const drawerPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    p: 3,
    pb: 0,
    borderRight: `1px solid ${colors.borderLight}`,
  };

  const sidebar = (
    <Sidebar
      tenantCode={tenantCode}
      tenantName={tenant?.tenant_name}
      onClose={isCompact ? () => setMobileOpen(false) : undefined}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isCompact ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
        >
          {sidebar}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: colors.bgPage,
          minHeight: '100vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          width: isCompact ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
        }}
      >
        {/* bare-chrome: no TopBar — analytics clients see only the sidebar nav,
            no env picker, version selector, search bar, or Publish button. */}
        {!bareChrome && (
          <TopBar
            tenantCode={tenantCode || ''}
            isCompact={isCompact}
            onOpenDrawer={() => setMobileOpen(true)}
            onOpenSearch={() => setSearchOpen(true)}
          />
        )}

        {/* Env picker / cross-env chrome isn't meaningful without an env
            dropdown — bare-chrome users never see one (decisions §11). */}
        {!bareChrome && (
          <>
            {/* WU11 — appears only when the env dropdown is set to a non-editable
                env. Sticky banner reminds the user the data is read-only. */}
            <ReadOnlyBanner />

            {/* Graceful "Couldn't reach {env}" notice when a remote env's reads
                fail (env-proxy upstream down), instead of per-page generic errors. */}
            <CrossEnvBanner />
          </>
        )}

        {/* key forces the entire content area to remount on pathname change so
            sibling routes with overlapping prefixes (actions vs actions/:taskCode)
            always get a fresh Outlet on navigation. */}
        <Box key={location.pathname} sx={{ flex: 1, p: isCompact ? 2 : 4, pt: bareChrome ? 4 : 2 }}>
          <ScrollToTop />
          {/* Phase 3 — tags routed content with data-read-only on a non-editable
              env (display:contents marker; does NOT block browsing). */}
          <ReadOnlyGuard>
            <Outlet />
          </ReadOnlyGuard>
        </Box>
      </Box>

      {!bareChrome && <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />}
    </Box>
  );
};

export default TenantLayout;
